import { useState, useEffect } from 'react';
import { getSharedResearchersService } from '../services';

interface UseResearcherRoleOptions {
    /** UUID of the investigation */
    uuidInvestigation: string;
    /** UUID of the researcher whose role to resolve */
    uuidResearcher?: string;
    /** Whether the hook should fetch (default: true) */
    enabled?: boolean;
}

/**
 * SHER-224 — resolve a researcher's role NAME within an investigation
 * (e.g. "DOCTOR", "NURSE", "ADMIN", or a custom role name).
 *
 * The role is not part of a patient submission's payload, so it is looked up
 * from the investigation's shared-researcher list. Used to label PDF
 * signatures with the uploader's real role instead of a hardcoded
 * "Médecin Prescripteur". Returns null while loading or if the role can't be
 * determined — callers must treat null as "no title".
 */
export const useResearcherRole = ({
    uuidInvestigation,
    uuidResearcher,
    enabled = true,
}: UseResearcherRoleOptions): string | null => {
    const [roleName, setRoleName] = useState<string | null>(null);

    useEffect(() => {
        if (!enabled || !uuidInvestigation || !uuidResearcher) {
            setRoleName(null);
            return;
        }
        let cancelled = false;
        getSharedResearchersService(uuidInvestigation)
            .then((data: any) => {
                if (cancelled) return;
                const list = data?.sharedResearchers || data?.researchers
                    || (Array.isArray(data) ? data : []);
                const match = Array.isArray(list)
                    ? list.find((r: any) => r && r.uuid === uuidResearcher)
                    : null;
                setRoleName(match?.role?.name || null);
            })
            .catch(() => {
                if (!cancelled) setRoleName(null);
            });
        return () => { cancelled = true; };
    }, [uuidInvestigation, uuidResearcher, enabled]);

    return roleName;
};
